/**
 * The (!) button, and what it opens.
 *
 * Every dashboard, list, record, panel, chart and tile carries one. The order
 * inside is fixed: purpose, then logic, then calculation, then what the surface
 * deliberately excludes, then the caveat. That order is the point of the
 * feature. A user who asks why a figure says what it says is answered in the
 * same shape everywhere, and a surface whose explainer is missing says so
 * rather than opening an empty panel, because a page that claims an
 * explanation and then withholds it is worse than one that never offered.
 *
 * The content is fetched rather than bundled: explainers are catalogue rows,
 * and an explainer edited in the database has to reach the screen without a
 * rebuild of this application.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '../api/client'
import { useSession } from '../app/session'
import { Drawer } from './Drawer'

interface ExplainContext {
  explain: (sectionKey: string, title?: string) => void
}

const Ctx = createContext<ExplainContext>({ explain: () => {} })

export function useExplain() { return useContext(Ctx).explain }

export function ExplainProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<{ key: string; title: string } | null>(null)
  const explain = useCallback((sectionKey: string, title?: string) => {
    setTarget({ key: sectionKey, title: title || 'Purpose, logic and calculation' })
  }, [])
  const value = useMemo(() => ({ explain }), [explain])

  return (
    <Ctx.Provider value={value}>
      {children}
      <Drawer title={target?.title || ''} open={Boolean(target)}
              onClose={() => setTarget(null)}>
        {target && <ExplainBody sectionKey={target.key} />}
      </Drawer>
    </Ctx.Provider>
  )
}

function ExplainBody({ sectionKey }: { sectionKey: string }) {
  const { lbl, d } = useSession()
  const { data, error, isLoading } = useQuery({
    queryKey: ['explain', sectionKey],
    queryFn: () => api.explain(sectionKey),
    staleTime: 300_000,
  })

  if (isLoading) return <p className="muted">Loading…</p>
  if (error) {
    return (
      <p className="ex-missing">
        That could not be loaded. The section may be outside your access, or the
        server may be unreachable.
      </p>
    )
  }
  if (!data || data.missing) {
    return (
      <p className="ex-missing">
        {data?.detail || `No explainer is registered for ${sectionKey}. That is a
        defect: every section carries one, and the gap is reported by the
        coverage view.`}
      </p>
    )
  }

  const e = data.explainer!
  const extra = e as unknown as Record<string, string | null>

  return (
    <div className="ex-body">
      <h4>Purpose</h4>
      <p>{e.purpose_en}</p>
      {e.purpose_ar && <div className="ar" dir="rtl">{e.purpose_ar}</div>}

      <h4>Logic</h4>
      <p>{e.logic_en}</p>
      {extra.logic_ar && <div className="ar" dir="rtl">{extra.logic_ar}</div>}

      {e.calculation_en && (
        <>
          <h4>Calculation</h4>
          <p>{e.calculation_en}</p>
          {extra.formula && <pre className="formula">{extra.formula}</pre>}
          {extra.worked_example && (
            <div className="worked"><b>Worked example.</b> {extra.worked_example}</div>
          )}
        </>
      )}

      {e.exclusions && (
        <>
          <h4>What this deliberately excludes</h4>
          <div className="excl">{e.exclusions}</div>
        </>
      )}

      {e.caveats && (
        <>
          <h4>Caveat</h4>
          <div className="cav">{e.caveats}</div>
        </>
      )}

      {e.data_sources && e.data_sources.length > 0 && (
        <>
          <h4>Where the numbers come from</h4>
          <div className="srcs">
            {e.data_sources.map((s) => <code key={s}>{s}</code>)}
          </div>
        </>
      )}

      {data.related && data.related.length > 0 && (
        <>
          <h4>Related sections</h4>
          <div className="srcs">
            {data.related.map((r) => <code key={r.section_key}>{r.name_en}</code>)}
          </div>
        </>
      )}

      <div className="ex-meta">
        <span className="b muted">{e.section_key}</span>
        <span className="b muted">{e.section_type}</span>
        {e.joget_element && <span className="b info">Joget: {e.joget_element}</span>}
        {e.decision_affecting && (
          <span className="b warn">affects funding decisions</span>
        )}
        {(e.data_classes || []).map((c) => (
          <span className="b muted" key={c}>{c}</span>
        ))}
        {data.grant && (
          <>
            <span className="b good">your actions: {data.grant.actions.join(', ')}</span>
            <span className="b muted">your row scope: {lbl(data.grant.row_scope)}</span>
          </>
        )}
        {extra.last_reviewed_at && (
          <span className="b muted">reviewed {d(extra.last_reviewed_at)}</span>
        )}
        {extra.version && <span className="b muted">explainer v{extra.version}</span>}
      </div>
    </div>
  )
}

/**
 * The button itself.
 *
 * `size` distinguishes the page-level button beside a heading from the
 * panel-level buttons on cards, tiles and charts. Both exist because a page
 * explainer answers what the surface is for, and a panel explainer answers how
 * that panel's figure was worked out; collapsing them would answer the first
 * question twice and the second never.
 */
export function ExplainButton({ sectionKey, title, size = 'sm' }: {
  sectionKey: string
  title?: string
  size?: 'sm' | 'lg'
}) {
  const explain = useExplain()
  const label = `Purpose, logic and calculation${title ? ` — ${title}` : ''}`
  return (
    <button className={size === 'lg' ? 'ibtn lg' : 'ibtn'} type="button"
            onClick={(e) => { e.stopPropagation(); explain(sectionKey, title) }}
            aria-label={label} title="Purpose · logic · calculation">!</button>
  )
}
